import { GET_USER_PRODUCT } from '@/modules/productManagement/endpoints'
import ApiErrorFactory from '@/entities/errors/api_errors/ApiErrorsFactory'
import AxiosFactory from '@/entities/security/AxiosFactory'
import {
  RebuildRequest,
  BackupRequest,
  RestoreBackupRequest,
  UpgradeOptionsRequest,
  ValidateOptionsRequest,
  UpgradePackageRequest,
  ResourceUsageRequest,
  AutorefillRequest,
  LowCreditRequest,
  AlarmRequest,
  CsrRequest,
  ValidationEmailRequest,
  ChangeValidationEmailRequest,
  RevalidateSslRequest,
  ReissueSslRequest,
  DeleteSecurityGroupRequest,
  CreateSecurityGroupRequest,
} from '@/modules/productManagement/types/store/ProductManagementStoreI'

export default class ProductApi {
  async loadService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id)
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async unpauseService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/unpause')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async pauseService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/pause')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async startService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/start')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async stopService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/stop')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async softRebootService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/reboot/soft')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async hardRebootService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/reboot/hard')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async suspendService(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/suspend')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async rebuildService(req: RebuildRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(GET_USER_PRODUCT + req.id + '/rebuild', {
        image_id: req.imageId,
      })
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async backupProduct(req: BackupRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(GET_USER_PRODUCT + req.id + '/backup/create', {
        name: req.name,
      })
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async restoreBackup(req: RestoreBackupRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/backup/restore/' + req.backupId,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadPublicKey(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/key/public')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadPrivateKey(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/key/private')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async requestCancellation(req: { id: string; reason: string; type: string }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + req.id + '/cancel-request', {
        reason: req.reason,
        type: req.type,
      })
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadSecurityGroup(id: number) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/security-group')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async createSecurityGroup(id: number, req: CreateSecurityGroupRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(GET_USER_PRODUCT + id + '/security-group', req)
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async deleteSecurityGroup(req: DeleteSecurityGroupRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().delete(
        GET_USER_PRODUCT + req.id + '/security-group/' + req.securityGroupId,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async getUpgradeOptions(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/upgrade/options')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async patchUpgradeOptions(req: UpgradeOptionsRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + req.id + '/upgrade/options', {
        paymentmethod: req.paymentmethod,
        applyFunds: req.applyFunds,
        configoptions: req.configoptions,
      })
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async postValidateOptions(req: ValidateOptionsRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(GET_USER_PRODUCT + req.id + '/upgrade/options', {
        configoptions: req.configoptions,
      })
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async patchUpgradePackage(req: UpgradePackageRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + req.id + '/upgrade/package', {
        paymentmethod: req.paymentmethod,
        newproductbillingcycle: req.newproductbillingcycle,
        newproductid: req.newproductid,
        applyFunds: req.applyFunds,
      })
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async getUpgradePackage(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/upgrade/package')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadResourceUsageTable(req: ResourceUsageRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + req.id + '/resource/table', {
        params: { page: req.page },
      })
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadResourceUsageGraph(req: ResourceUsageRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + req.id + '/resource/graph', {
        params: { startDate: req.startDate, endDate: req.endDate },
      })
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadAutorefill(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/autorefill')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async updateAutorefill(req: AutorefillRequest) {
    const data = {
      autoRefillUnder: req.autoRefillUnder,
      autoRefillValue: req.autoRefillValue,
    }
    try {
      const response = await AxiosFactory.createAxiosInstance().put(GET_USER_PRODUCT + req.id + '/autorefill', data)
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async deleteAutorefill(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().delete(GET_USER_PRODUCT + id + '/autorefill')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadLowCredit(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/low-credit')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async updateLowCredit(req: LowCreditRequest) {
    const data = {
      creditAmount: req.creditAmount,
    }
    try {
      const response = await AxiosFactory.createAxiosInstance().post(GET_USER_PRODUCT + req.id + '/low-credit', data)
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async deleteLowCredit(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().delete(GET_USER_PRODUCT + id + '/low-credit')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadAlarms(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/remainders')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async createAlarms(obj: AlarmRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + obj.id + '/remainders',
        obj.data,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async updateAlarms(obj: AlarmRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(
        GET_USER_PRODUCT + obj.id + '/remainders/' + obj.reminderId,
        obj.data,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async deleteAlarms(obj: AlarmRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().delete(
        GET_USER_PRODUCT + obj.id + '/remainders/' + obj.reminderId,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async deleteAlarm(obj: AlarmRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().delete(
        GET_USER_PRODUCT + obj.id + '/remainders' + obj.remainderId,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async changeAlarmStatus(obj: AlarmRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(
        GET_USER_PRODUCT + obj.id + '/remainders/' + obj.reminderId + '/change-status',
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadAlarmsLimiter(obj: AlarmRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(
        GET_USER_PRODUCT + obj.id + '/remainders/' + obj.remainderId + '/limiter',
      )
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async updateAlarmsLimiter(obj: AlarmRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(
        GET_USER_PRODUCT + obj.id + '/remainders/' + obj.reminderId + '/limiter',
        obj.data,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async loadConvert(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/convert')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async executeConvert(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/convert')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async sendCredentials(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().patch(GET_USER_PRODUCT + id + '/resend-confirmation')
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async generateCsr(req: CsrRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + req.id + '/ssl/generate-csr', {
        params: req.body,
      })
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async loadSslServerTypes(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(
        GET_USER_PRODUCT + id + '/ssl/configure/get-servers',
      )
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async validateConfigureSsl(req: { id: string; body: Record<string, any> }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/configure/validate',
        req.body,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async validateReissueSsl(req: { id: string; body: Record<string, any> }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/reissue/validate',
        req.body,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async initDcvMethods(req: { id: string; body: Record<string, any> }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/configure/init-dcv-methods',
        req.body,
      )
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async reissueInitDcvMethods(req: { id: string; body: Record<string, any> }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/reissue/init-dcv-methods',
        req.body,
      )
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async finishSslConfiguration(req: { id: string; body: Record<string, any> & { privateKey?: string } }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/configure/finish',
        req.body,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async reissueFinishSslConfiguration(req: { id: string; body: Record<string, any> & { privateKey?: string } }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/reissue/finish',
        req.body,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async getPem(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/ssl/get-pem')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async getPrivateKey(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/ssl/get-private-key ')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async getValidationMethods(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/ssl/dcv-methods')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async setValidationMethods(req: { id: string; body: Record<string, any> }) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/dcv-methods',
        req.body,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async resendValidationEmail(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + id + '/ssl/validation/resend-validation-email',
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async getValidationEmail(req: ValidationEmailRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(
        GET_USER_PRODUCT + req.id + '/ssl/validation/get-validation-email?domain=' + req.domain,
      )
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async changeValidationEmail(req: ChangeValidationEmailRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/validation/change-validation-email',
        req.body,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async revalidateSsl(req: RevalidateSslRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/validation/revalidate',
        req.body,
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async reissueSsl(req: ReissueSslRequest) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + req.id + '/ssl/validation/reissue',
        req.body,
      )
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async getCsr(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/ssl/get-csr')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }

  async getCertificate(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().get(GET_USER_PRODUCT + id + '/ssl/get-ca')
      return response.data
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
  async sendCertificate(id: string) {
    try {
      const response = await AxiosFactory.createAxiosInstance().post(
        GET_USER_PRODUCT + id + '/ssl/send-certificate-email',
      )
      return response
    } catch (e) {
      throw ApiErrorFactory.create(e)
    }
  }
}
